import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, BookMarked } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Paywall from "@/components/Paywall";
import BundleHint from "@/components/BundleHint";
import { useI18n } from "@/context/I18nContext";
import { langDisplayName } from "@/lib/languages";

const PERSONAS = [
  { persona: "Mateo, your easygoing roommate", scenario: "chatting at home in the kitchen", emoji: "🏠", label: "Roommate", key: "roommate" },
  { persona: "Sofía, a friendly café barista", scenario: "ordering coffee at a busy café", emoji: "☕", label: "Café", key: "cafe" },
  { persona: "Luca, a market vendor", scenario: "buying fruit at a street market", emoji: "🍎", label: "Market", key: "market" },
  { persona: "Dr. Marin, a doctor", scenario: "a check-up at the clinic", emoji: "🩺", label: "Clinic", key: "clinic" },
];

export default function Roommate() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const [scene, setScene] = useState(PERSONAS[0]);
  const [messages, setMessages] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [vocab, setVocab] = useState([]);
  const [convId, setConvId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await api.post("/roommate/chat", {
        message: msg, persona: scene.persona, scenario: scene.scenario, conversation_id: convId,
      });
      setConvId(res.data.conversation_id);
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: res.data.reply, translation: res.data.reply_translation }]);
      if (res.data.corrections?.length) setCorrections((c) => [...res.data.corrections, ...c]);
      if (res.data.new_vocab?.length) setVocab((v) => [...res.data.new_vocab, ...v]);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(true);
      else toast.error(t("pm.common.messageFailed"));
      setMessages((m) => m.slice(0, -1));
    } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="font-cabinet font-extrabold text-3xl">LanguageRoommate</h1>
        <p className="text-gray-600">{t("pm.roommate.subtitle")}</p>
        <BundleHint id="roommate">{t("pm.roommate.hint")}</BundleHint>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          {PERSONAS.map((p) => (
            <button key={p.label} onClick={() => { setScene(p); setMessages([]); setConvId(null); }}
              className={`nb-tag ${scene.label === p.label ? "bg-lavender shadow-hard-sm" : "bg-white"}`} data-testid={`persona-${p.label.toLowerCase()}`}>
              {p.emoji} {t(`pm.roommate.personas.${p.key}.label`)}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-5">
          <div className="lg:col-span-2 nb-card p-0 flex flex-col h-[60vh]">
            <div className="flex-1 overflow-auto p-5 space-y-4 no-scrollbar" data-testid="chat-window">
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                  <Sparkles className="w-10 h-10" />
                  <p className="mt-2 font-cabinet font-bold">{scene.emoji} {t(`pm.roommate.personas.${scene.key}.scenario`)}</p>
                  <p className="text-sm">{t("pm.roommate.sayHello")}</p>
                </div>
              )}
              {messages.map((m) => (
                <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] border-2 border-ink rounded-2xl px-4 py-2 ${m.role === "user" ? "bg-lavender rounded-se-none" : "bg-gray-100 rounded-ss-none"}`}>
                    <p className="font-medium" dir="auto">{m.content}</p>
                    {m.translation && <p className="text-xs text-gray-500 mt-1" dir="auto">{m.translation}</p>}
                  </div>
                </motion.div>
              ))}
              {loading && <div className="flex justify-start"><div className="bg-gray-100 border-2 border-ink rounded-2xl rounded-ss-none px-4 py-3"><div className="flex gap-1"><Dot /><Dot d="0.2s" /><Dot d="0.4s" /></div></div></div>}
              <div ref={endRef} />
            </div>
              <div className="border-t-2 border-ink p-3 flex gap-2">
                <input className="nb-input flex-1" placeholder={user?.target_lang === "en" ? t("pm.roommate.typeIn", { lang: langDisplayName("en", locale) }) : t("pm.roommate.typeTarget")}
                  value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="chat-input" />
                <button onClick={send} disabled={loading} className="nb-btn-primary px-5" data-testid="chat-send"><Send className="w-5 h-5" /></button>
              </div>
          </div>

          <div className="space-y-6">
            <div className="nb-card p-5">
              <h3 className="font-cabinet font-bold flex items-center gap-2"><BookMarked className="w-5 h-5" /> {t("pm.roommate.wordsToday")}</h3>
              <div className="mt-3 space-y-2 max-h-44 overflow-auto no-scrollbar">
                {vocab.length ? vocab.map((v, i) => (
                  <div key={`${v.word}-${i}`} className="border-2 border-ink rounded-xl px-3 py-2 bg-mint/40">
                    <div className="font-cabinet font-bold" dir="auto">{v.word}</div>
                    <div className="text-xs text-gray-600" dir="auto">{v.translation}</div>
                  </div>
                )) : <p className="text-sm text-gray-400">{t("pm.roommate.newWordsHere")}</p>}
              </div>
            </div>
            <div className="nb-card p-5">
              <h3 className="font-cabinet font-bold">{t("pm.roommate.corrections")}</h3>
              <div className="mt-3 space-y-2 max-h-44 overflow-auto no-scrollbar">
                {corrections.length ? corrections.map((c, i) => (
                  <div key={`${c.original}-${i}`} className="text-sm border-2 border-ink rounded-xl p-2 bg-peach/40">
                    <span className="line-through text-gray-500" dir="auto">{c.original}</span> <span className="inline-block rtl:rotate-180">→</span> <span className="font-bold" dir="auto">{c.corrected}</span>
                    <p className="text-xs text-gray-600 mt-0.5">{c.explanation}</p>
                  </div>
                )) : <p className="text-sm text-gray-400">{t("pm.roommate.doingGreat")}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Paywall open={paywall} onClose={() => setPaywall(false)} reason={t("pm.common.proFeature", { name: "LanguageRoommate" })} />
    </AppLayout>
  );
}

const Dot = ({ d = "0s" }) => <span className="w-2 h-2 bg-ink rounded-full animate-bounce" style={{ animationDelay: d }} />;
